import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";

export default function AuthPage({ mode = "login" }) {
    const { refresh } = useAuth();
    const nav = useNavigate();
    const { t } = useLocale();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [ageConfirmed, setAgeConfirmed] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const isSignup = mode === "signup";

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (isSignup && (!termsAccepted || !ageConfirmed)) {
            setErr(t("auth.err_required"));
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(
                isSignup ? "/auth/signup" : "/auth/login",
                isSignup
                    ? { email, password, name, terms_accepted: termsAccepted, age_confirmed: ageConfirmed, terms_version: "v1" }
                    : { email, password }
            );
            if (data.token) localStorage.setItem("echoes_jwt", data.token);
            await refresh();
            // Analytics — differentiate signup (new account) from login.
            if (isSignup) {
                // Fire before navigation so sendBeacon can flush.
                try {
                    const { track } = await import("@/lib/track");
                    track("signup_completed");
                } catch { /* noop */ }
            }
            nav(isSignup ? "/onboarding/consent" : "/dashboard");
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d === "legal:age_required") setErr(t("auth.err_age"));
            else if (d === "legal:terms_required") setErr(t("auth.err_terms"));
            else setErr(typeof d === "string" ? d : t("auth.err_generic"));
        } finally {
            setBusy(false);
        }
    };

    const googleSignIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <div className="max-w-md mx-auto px-6 pt-16 pb-24 animate-fade-in">
                <div className="flex items-center gap-3 mb-6">
                    <BrandMark size={26} />
                    <p className="label-eyebrow" data-testid="auth-eyebrow">
                        {isSignup ? t("auth.eyebrow_signup") : t("auth.eyebrow_login")}
                    </p>
                </div>
                <h1 className="h-serif text-4xl md:text-5xl mb-10 leading-tight">
                    {isSignup ? <>{t("auth.title_signup_a")} <span className="italic text-ink-gold">{t("auth.title_signup_i")}</span></>
                             : <>{t("auth.title_login_a")} <span className="italic text-ink-gold">{t("auth.title_login_i")}</span></>}
                </h1>

                <button
                    type="button"
                    onClick={googleSignIn}
                    data-testid="auth-google-btn"
                    className="btn-secondary w-full flex items-center justify-center gap-3 mb-8"
                >
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                    {t("auth.google_cta")}
                </button>

                <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-ink-border"></div></div>
                    <div className="relative flex justify-center text-xs"><span className="bg-ink-deep px-3 text-ink-textLow tracking-widest uppercase">{t("auth.divider")}</span></div>
                </div>

                <form onSubmit={submit} className="space-y-7">
                    {isSignup && (
                        <div>
                            <label className="label-eyebrow" htmlFor="name">{t("auth.name_label")}</label>
                            <input
                                id="name"
                                data-testid="auth-name"
                                className="input-line mt-2"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder=""
                                required
                            />
                        </div>
                    )}
                    <div>
                        <label className="label-eyebrow" htmlFor="email">{t("auth.email_label")}</label>
                        <input
                            id="email"
                            type="email"
                            data-testid="auth-email"
                            className="input-line mt-2"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label className="label-eyebrow" htmlFor="password">{t("auth.password_label")}</label>
                        <input
                            id="password"
                            type="password"
                            data-testid="auth-password"
                            className="input-line mt-2"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            minLength={6}
                            required
                        />
                        {!isSignup && (
                            <div className="text-right mt-2">
                                <Link
                                    to="/auth/forgot-password"
                                    data-testid="auth-forgot-link"
                                    className="text-xs text-ink-textLow hover:text-ink-gold transition-colors italic"
                                >
                                    Password dimenticata?
                                </Link>
                            </div>
                        )}
                    </div>

                    {err && <p data-testid="auth-error" className="text-ink-danger text-sm font-serif italic">{err}</p>}

                    {isSignup && (
                        <div className="space-y-4 pt-2" data-testid="auth-legal-block">
                            <div className="border-l-2 border-ink-gold/60 pl-4 py-1" data-testid="vulnerable-warning">
                                <p className="font-serif italic text-ink-textMid text-sm leading-relaxed">
                                    {t("auth.vulnerable")}
                                </p>
                            </div>
                            <label className="flex items-start gap-3 cursor-pointer group" data-testid="auth-ai-label">
                                <input
                                    type="checkbox"
                                    data-testid="auth-terms-checkbox"
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                    className="mt-1.5 w-4 h-4 accent-ink-gold shrink-0"
                                />
                                <span className="text-sm text-ink-textMid leading-relaxed">
                                    {t("auth.ai_check_a")} <strong className="text-ink-textHigh">{t("auth.ai_check_strong")}</strong> {t("auth.ai_check_b")}
                                </span>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer group" data-testid="auth-age-label">
                                <input
                                    type="checkbox"
                                    data-testid="auth-age-checkbox"
                                    checked={ageConfirmed}
                                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                                    className="mt-1.5 w-4 h-4 accent-ink-gold shrink-0"
                                />
                                <span className="text-sm text-ink-textMid leading-relaxed">
                                    {t("auth.age_check_a")} <strong className="text-ink-textHigh">{t("auth.age_check_strong")}</strong>{t("auth.age_check_b")}
                                </span>
                            </label>
                            <p className="text-xs text-ink-textLow leading-relaxed pl-7">
                                {t("auth.legal_read")}{" "}
                                <Link to="/legal" target="_blank" className="text-ink-gold underline underline-offset-4">{t("auth.legal_link")}</Link>.
                            </p>
                        </div>
                    )}

                    <button disabled={busy} data-testid="auth-submit" className="btn-primary w-full">
                        {busy ? "…" : (isSignup ? t("auth.submit_signup") : t("auth.submit_login"))}
                    </button>
                </form>

                <p className="text-sm text-ink-textLow mt-8 text-center">
                    {isSignup ? (
                        <>{t("auth.have_account")} <Link to="/login" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold">{t("auth.go_signin")}</Link></>
                    ) : (
                        <>{t("auth.first_time")} <Link to="/signup" className="text-ink-textHigh underline underline-offset-4 decoration-ink-gold">{t("auth.go_signup")}</Link></>
                    )}
                </p>

                <div className="mt-10 pt-6 border-t border-ink-border text-center flex flex-wrap justify-center gap-5 text-xs text-ink-textMid" data-testid="auth-legal-footer">
                    <Link to="/legal" className="hover:text-ink-textHigh">{t("auth.footer_legal")}</Link>
                </div>
            </div>
        </div>
    );
}
